// Pure, dependency-free logic for the paid-entry / activation handoff.
// No imports (api functions are injected) so it is trivially unit-testable.

// Which authenticated surface to render from the customer-safe lifecycle projection.
export function decideView(lifecycle) {
  if (!lifecycle) return "legacy";
  if (lifecycle.current_cycle) return "active";
  if ((lifecycle.available_entitlement_count || 0) > 0) return "ready";
  return "legacy";
}

// Map the existing user profile 1:1 onto the backend activation focus contract (FocusSnapshot /
// ActivateFocusRequest). No invented data: absent fields stay empty strings.
export function focusFromProfile(profile = {}) {
  const p = profile || {};
  return {
    niche: p.niche || "",
    customNiche: p.customNiche || "",
    brand: p.brand || "",
    audience: p.audience || "",
    audienceShort: p.audienceShort || "",
    searchableTitle: p.searchableTitle || "",
    bio: p.bio || "",
  };
}

// Essential focus fields required for a meaningful cycle. If any are missing we prompt the customer
// to complete their profile rather than activating with empty/placeholder content.
export function missingFocusFields(focus = {}) {
  const missing = [];
  if (!(focus.brand || "").trim()) missing.push("brand");
  if (!(focus.searchableTitle || "").trim()) missing.push("searchableTitle");
  if (!(focus.audience || "").trim()) missing.push("audience");
  if (!((focus.niche || "").trim() || (focus.customNiche || "").trim())) missing.push("niche");
  if (!(focus.bio || "").trim()) missing.push("bio");
  return missing;
}

export const isFocusReady = (focus) => missingFocusFields(focus).length === 0;

// Editable starting values for the confirmation form, seeded from the existing profile. These are
// ONLY prefill defaults — the customer must review/confirm before they are submitted. Used by the
// explicit "Use my last setup" action (never as the silent default for a new cycle).
export function initialFocusForm(profile = {}) {
  const p = profile || {};
  return {
    brand: p.brand || "",
    searchableTitle: p.searchableTitle || "",
    audience: p.audience || "",
    audienceShort: p.audienceShort || "",
    niche: p.niche || p.customNiche || "",
    bio: p.bio || "",
  };
}

// Default editable values for a NEW cycle's pre-activation intake. Stable account identity
// (Name / Brand) is preserved so the customer need not re-type it, but every FOCUS-SPECIFIC field
// (Creative Work/Niche, Who You Help, Who You Help Short, Searchable Title, AEO-ready Bio) starts
// FRESH. The prior cycle's focus is never silently reused; the customer defines the new focus, or
// intentionally restores it via "Use my last setup" (which calls initialFocusForm).
export function newCycleInitialForm(profile = {}) {
  const p = profile || {};
  return {
    brand: p.brand || "",   // stable identity — preserved
    searchableTitle: "",    // focus-specific — fresh
    audience: "",           // focus-specific — fresh
    audienceShort: "",      // focus-specific — fresh
    niche: "",              // focus-specific — fresh
    bio: "",                // focus-specific — fresh
  };
}

// Build the activation focus from the CONFIRMED form values (never an automatic reread of the
// profile). Only reviewed fields are submitted; unreviewed fields stay empty by design.
export function focusFromForm(form = {}) {
  return {
    niche: (form.niche || "").trim(),
    customNiche: "",
    brand: (form.brand || "").trim(),
    audience: (form.audience || "").trim(),
    audienceShort: (form.audienceShort || "").trim(),
    searchableTitle: (form.searchableTitle || "").trim(),
    bio: (form.bio || "").trim(),
  };
}

// Explicit activation orchestration. Never auto-runs; called only on button click. On success it
// re-fetches the lifecycle so the caller renders from current_cycle. 409 conflicts are surfaced
// as customer-safe messages; nothing is retried automatically.
export async function runActivation({ focus, activateCycle, getLifecycle }) {
  try {
    await activateCycle(focus);
    const lifecycle = await getLifecycle();
    return { ok: true, lifecycle };
  } catch (e) {
    const status = e && e.response && e.response.status;
    const detail = e && e.response && e.response.data && e.response.data.detail;
    if (status === 409 && detail) {
      return { ok: false, conflict: { code: detail.code, message: detail.message } };
    }
    return {
      ok: false,
      conflict: { code: "activation_failed", message: "We couldn't start your cycle. Please try again." },
    };
  }
}
